import type { ErrorCause } from '@shared/types';
import { error as errorResponse, type NumericRange } from '@sveltejs/kit';

export function parseResponse<D, E = Error>(
    clientResponse: { response: Response; data?: D; error?: E }
): D {
    const { response, data, error } = clientResponse;

    if (response.status !== 200 && response.status !== 201) {
        console.error(`Error get ${response.url}`, response.statusText);
        import('@sentry/sveltekit?inline').then(Sentry => {
            Sentry.captureException(new Error(`Error get ${response.url}`), {
                contexts: {
                    errorInfo: {
                        code: response.status,
                        message: (error as Error)?.message ?? response.statusText,
                        cause: ((error as unknown as Error)?.cause as ErrorCause),
                        client: true,
                    },
                },
            });
        });

        throw errorResponse(response.status as NumericRange<400, 599>, {
            code: response.status,
            message: (error as Error)?.message ?? response.statusText,
            cause: ((error as unknown as Error)?.cause as ErrorCause),
        });
    }

    return data as unknown as D;
}

export function parseQuery<T extends Record<string, unknown>>(
    searchParams: URLSearchParams,
    keys: (keyof T)[],
    defaultParams?: Partial<T>
): T {
    const paramEntries = [ ...searchParams.entries()].filter(([k]) => keys.includes(k));
    const params = Object.fromEntries(paramEntries) as T;
    if (defaultParams) {
        const defaultParamKeys: (keyof T)[] = Object.keys(defaultParams);
        defaultParamKeys.forEach(k => {
            if (!params[k]) {
                params[k] = defaultParams[k] as T[string];
            }
        });
    }
    return params;
}
